import useActions from "./useActions";
import {
  LockKeyhole,
  RefreshCcw,
  ExternalLink,
  TriangleAlert,
} from "lucide-react";

import { Button, Input, Textarea } from "@/components/ui";
import { Dialog, Loader } from "@/components";
import { formatAmount } from "@/lib/utils";

const Actions = ({
  isValidationCompleted = false,
}: {
  isValidationCompleted?: boolean;
}) => {
  const {
    isLocking,
    openLockDialog,
    currentFinanceYear,
    onLockYear,
    onConfirmLockYear,
    setOpenLockDialog,
    isCurrentYearLock,
    isCurrentYearClosed,
    isOpenNewYearPending,
    isCarryForwardPending,
    onCarryForward,
    openCarryForwardDialog,
    setOpenCarryForwardDialog,
    onNextCarryForward,
    isLoadingTrialBalData,
    trialBalanceData,
    isLoadingNetProfitLoss,
    netProfitLoss,
    onConfirmCarryForward,
    onReopenFiscalYear,
    onReopenReverseFiscalYear,
    openReopenDialog,
    setOpenReopenDialog,
    reopenText,
    onChangeReopenText,
    onConfirmReopen,
    onOpenNewYear,
    onConfirmOpenNewYear,
    openNewYearDialog,
    setOpenNewYearDialog,
    isFiscalDataLoading,
    fiscalYearData,
    hasEditAccess
  } = useActions();

  return (
    <>
      <div className="bg-blue-50 border border-blue-600 rounded-lg px-4 sm:px-[30px] py-4 sm:py-[18px]">
        <div className="mb-5">
          <h3 className="text-base font-semibold text-gray-900">Actions</h3>
          <p className="text-sm text-gray-500 mt-1">
            Close Year uses a guided wizard (Lock & Carry Forward). Locked years are read-only.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Button
            disabled={isCurrentYearLock || isCurrentYearClosed || !hasEditAccess}
            onClick={onLockYear}
            className="w-full h-[42px] flex items-center justify-center gap-2 disabled:bg-gray-300 disabled:text-gray-600 bg-primary text-white"
          >
            {isCarryForwardPending ? <Loader variant="secondary" /> : <></>}
            <LockKeyhole size={16} />
            Close Year (Lock Year)
          </Button>

      
          <Button
            disabled={
              !isCurrentYearLock ||
              !isValidationCompleted ||
              isCarryForwardPending || !hasEditAccess
            }
            className="w-full h-[42px] flex items-center justify-center gap-2 bg-gray-300 text-gray-600 enabled:bg-primary enabled:text-white"
            onClick={onCarryForward}
          >
            {isCarryForwardPending ? <Loader variant="secondary" /> : <></>}
            <RefreshCcw size={16} />
            Carry Forward (Financial Year Roll)
          </Button>

      
          <Button
            onClick={onOpenNewYear}
            disabled={isFiscalDataLoading || fiscalYearData || !hasEditAccess}
            className="w-full h-[42px] flex items-center justify-center gap-2 disabled:bg-gray-300 disabled:text-gray-600 bg-primary text-white"
          >
            <ExternalLink size={16} />
            Add FY {Number(currentFinanceYear) + 1} (Allow Operations)
          </Button>

          
          {isCurrentYearClosed || isCurrentYearLock ? <div className="w-full min-h-[42px] border border-red-600 rounded-md flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 px-3 py-2">
            <div className="flex items-center gap-2 text-red-600 font-semibold text-sm">
              <TriangleAlert size={16} />
              Danger Zone
            </div>

            {isCurrentYearLock ? 
              <Button
                onClick={onReopenFiscalYear}
                disabled={!hasEditAccess}
                className="h-[32px] px-3 bg-red-600 hover:bg-red-700 text-white text-xs w-full sm:w-auto"
              >
                Reopen Fiscal Year
              </Button> : <></>}
            {isCurrentYearClosed ? 
              <Button
                onClick={onReopenReverseFiscalYear}
                disabled={!hasEditAccess}
                className="h-[32px] px-3 bg-red-600 hover:bg-red-700 text-white text-xs w-full sm:w-auto"
              >
                Reopen & Reverse CF {Number(currentFinanceYear)}
            </Button> : <></>}
          </div> : <></>}
        </div>
      </div>

      {openLockDialog && (
        <Dialog
          open={openLockDialog}
          setOpen={setOpenLockDialog}
          title={
            <div className="text-2xl font-bold text-primary">
              Year End Closing
              <div className="h-1 w-[60%] bg-primary rounded-full" />
            </div>
          }
          saveText="Confirm & Lock"
          cancelText="No"
          onSave={onConfirmLockYear}
          isLoading={isLocking}
        >
          <p className="text-sm text-gray-600">
            You are about to make <b>FY {currentFinanceYear}</b> Read-Only.
            <br />
            No further postings will be allowed.
            <br />
            <br />
            Note: This action does not perform Carry Forward; it only locks the period.
          </p>
        </Dialog>
      )}

      {openCarryForwardDialog?.step1 ? (
        <Dialog
          open={openCarryForwardDialog?.step1}
          setOpen={(val: boolean) => setOpenCarryForwardDialog((prev: any) => ({...prev, step1: val}))}
          title={
            <div className="text-2xl font-bold text-primary">
              Calculate Trial Balance
              <div className="h-1 w-[60%] bg-primary rounded-full" />
            </div>
          }
          saveText="Next"
          cancelText="No"
          onSave={() => onNextCarryForward("step2")}
        >
          {isLoadingTrialBalData ? <Loader /> : 
            <p className="text-sm text-gray-600">
              The Total Debits are equal to the Total Credits for FY {currentFinanceYear}.
              
              <table className="border-1 mt-3">
                <tr>
                  <th className="border-1 border-black p-2">Description</th>
                  <th className="border-1 border-black p-2">Value</th>
                </tr>
                <tr>
                  <td className="border-1 border-black p-2">Total Debits(FY {currentFinanceYear})</td>
                  <td className="border-1 border-black p-2">{formatAmount(trialBalanceData?.totalDebits || "0")}</td>
                </tr>
                <tr>
                  <td className="border-1 border-black p-2">Total Credits(FY {currentFinanceYear})</td>
                  <td className="border-1 border-black p-2">{formatAmount(trialBalanceData?.totalCredits || "0")}</td>
                </tr>
                <tr>
                  <td className="border-1 border-black p-2 font-bold">Variance(Out of balance)</td>
                  <td className="border-1 border-black p-2 font-bold">{formatAmount(trialBalanceData?.variance || "0")}</td>
                </tr>
              </table>
            </p>}
        </Dialog>
      ): openCarryForwardDialog.step2 ? 
        <Dialog
          open={openCarryForwardDialog?.step2}
          setOpen={(val: boolean) => setOpenCarryForwardDialog((prev: any) => ({...prev, step2: val}))}
          title={
            <div className="text-2xl font-bold text-primary">
               Net Profit/Loss Calculation
              <div className="h-1 w-[60%] bg-primary rounded-full" />
            </div>
          }
          saveText="Next"
          cancelText="No"
          onSave={() => onNextCarryForward("step3")}
        >
          {isLoadingNetProfitLoss ? <Loader /> : 
            <p className="text-sm text-gray-600">
              <i>
                <span>Total Income (Money In / Revenue)</span><br/>
                <b>Total Income = {formatAmount(netProfitLoss?.totalIncome || "0")}</b>
              </i><br/><br/>

              <i>
                <span>Total Expenses (Money Out / Spending)</span><br/>
                <b>Total Expenses = {formatAmount(netProfitLoss?.totalExpenses || "0")}</b>
              </i>
              
              <table className="border-1 mt-3">
                <tr>
                  <th className="border-1 border-black p-2">Type</th>
                  <th className="border-1 border-black p-2">Amount</th>
                  <th className="border-1 border-black p-2">Logic</th>
                </tr>
                <tr>
                  <td className="border-1 border-black p-2 font-bold">Total Income</td>
                  <td className="border-1 border-black p-2">{formatAmount(netProfitLoss?.totalIncome || "0")}</td>
                  <td className="border-1 border-black p-2">(Credits Balances)</td>
                </tr>
                <tr>
                  <td className="border-1 border-black p-2 font-bold">Total Expense</td>
                  <td className="border-1 border-black p-2">{formatAmount(netProfitLoss?.totalExpenses || "0")}</td>
                  <td className="border-1 border-black p-2">(Debits Balances)</td>
                </tr>
                <tr>
                  <td className="border-1 border-black p-2 font-bold">Net Result</td>
                  <td className="border-1 border-black p-2 font-bold">{formatAmount(netProfitLoss?.netResult || "0")}</td>
                  <td className="border-1 border-black p-2 font-bold">Net Surplus ({Number(netProfitLoss?.netResult) >= 0 ? "Profit" : "Loss"})</td>
                </tr>
              </table>
            </p>}
        </Dialog>
       : openCarryForwardDialog.step3 ?
          <Dialog
            open={openCarryForwardDialog?.step3}
            setOpen={(val: boolean) => setOpenCarryForwardDialog((prev: any) => ({...prev, step3: val}))}
            title={
              <div className="text-2xl font-bold text-primary">
                  The Retained Earnings Transfer
                <div className="h-1 w-[60%] bg-primary rounded-full" />
              </div>
            }
            saveText="Confirm & Proceed"
            cancelText="No"
            onSave={onConfirmCarryForward}
          >
            {isLoadingNetProfitLoss ? <Loader /> : 
              <p className="text-sm text-gray-600">
                <i>
                  The {formatAmount(netProfitLoss?.netResult || "0")} from {currentFinanceYear} will be moved to Account 9000 
                  (Retained Earnings). All Balance Sheet accounts (Assets, Liabilities, Equity) 
                  will be 'Lift and Shifted' to FY {Number(currentFinanceYear) + 1} Period 0.
                </i>
              </p>}
          </Dialog>
       : <></>}

  
        {openNewYearDialog && (
          <Dialog
            open={openNewYearDialog}
            setOpen={setOpenNewYearDialog}
            title={
              <div className="text-2xl font-bold text-red-600">
                Critical Warning: Open FY {Number(currentFinanceYear) + 1} (Allow Operations)
                <div className="h-1 w-[20%] bg-red-600 rounded-full mt-1" />
              </div>
            }
            saveText="Confirm & Open"
            cancelText="No"
            onSave={onConfirmOpenNewYear}
            isLoading={isOpenNewYearPending}
          >
            <div className="mt-3 text-[13px] text-red-500">
            This will create FY {Number(currentFinanceYear) + 1} and allow billing, collections, vendor invoices, and vendor payments in FY {Number(currentFinanceYear) + 1} even if FY {currentFinanceYear} is not carry-forwarded.
            </div>
          </Dialog>
        )}

    
        {openReopenDialog && (
          <Dialog
            open={openReopenDialog}
            setOpen={setOpenReopenDialog}
            title={
              <div className="text-2xl font-bold text-red-600">
                Critical Warning: Reopening Closed Year
                <div className="h-1 w-[20%] bg-red-600 rounded-full mt-1" />
              </div>
            }
            saveText={reopenText?.isReversed ? `Reopen & Reverse CF ${currentFinanceYear}` : "Confirm & Open"}
            cancelText="No"
            onSave={onConfirmReopen}
          >
            <p className="text-sm text-red-600 mb-5">
              {reopenText?.isReversed
                ? `This will REVERSE all Period 0 Opening Balances for FY ${Number(currentFinanceYear) + 1} and reset the Retained Earnings transfer. FY ${currentFinanceYear} will return to an OPEN status. You will need to perform the 'Carry Forward' again after your corrections."`
                : `Reopening FY ${currentFinanceYear} will allow users to post new transactions and edit existing journals. You will be required to re-run all validations and re-lock the year before you can Carry Forward. Do you wish to proceed?`}
            </p>

            <div className="flex flex-col mb-6 gap-2">
              <label className="text-sm text-gray-600">
                Please type <b>"{reopenText?.isReversed ? "REVERSE" : "REOPEN"}"</b>
              </label>
              <Input
                value={reopenText?.confirmText}
                placeholder="Please Enter"
                onChange={(e: any) => onChangeReopenText('confirmText', e.target.value)}
                required
              />
            </div>

            <div className="flex flex-col mb-6 gap-2">
              <label className="text-sm text-gray-600">
                Please add the reason for reopening.
              </label>
              <Textarea
                rows={5}
                value={reopenText?.reason}
                placeholder="Please Enter"
                onChange={(e: any) => onChangeReopenText('reason', e.target.value)}
                required
              />
            </div>
          </Dialog>
        )}
    </>
  );
};

export default Actions;
